import { arrayOf, bool, number, oneOfType, shape, string } from "prop-types";

import styled from "styled-components/macro";

import FnrTypography from "FnrTypography/FnrTypography";

import FnrAccordion from "UI/FnrAccordion/FnrAccordion";

import { formatCurrencyWithCommas } from "Utilities/Number/formatNumberWithCommas";

import TierPricingProvider from "../TierPricingProvider";
import {
  useEntity,
  useMinTierCost,
  useSortedTierData,
  useSuggestedDonation,
} from "../tierPricingHooks";

const Tier = ({ amount, numberOfSlots, numberOfSlotsBelow }) => {
  const entity = useEntity();

  return (
    <>
      <FnrTypography color="textLight" data-cy="TierPriceViewer_Slots">
        {getTierText({ numberOfSlots, numberOfSlotsBelow, entity })}
      </FnrTypography>
      <FnrTypography color="textLight" data-cy="TierPriceViewer_Amount">
        ${formatCurrencyWithCommas(Number(amount) / 100)}
      </FnrTypography>
    </>
  );
};

const getTierText = ({ numberOfSlots, numberOfSlotsBelow, entity }) => {
  if (!numberOfSlots) return `${Number(numberOfSlotsBelow) + 1}+ ${entity}s`;

  if (numberOfSlots === 1) return `${numberOfSlots} ${entity}`;

  const singleSlotTier = numberOfSlotsBelow + 1 === numberOfSlots;
  if (singleSlotTier) return `${numberOfSlots} ${entity}s`;

  const slotStartNum = numberOfSlotsBelow ? numberOfSlotsBelow + 1 : 1;

  return `${slotStartNum} - ${numberOfSlots} ${entity}s`;
};

const Grid = styled("div")`
  display: grid;
  grid-template-columns: max-content auto;
  column-gap: 24px;
  row-gap: 8px;
  padding-bottom: 8px;
`;

const TierPriceViewerContents = () => {
  const sortedTierData = useSortedTierData();
  const minCost = useMinTierCost();
  const suggestedDonation = useSuggestedDonation();
  const entity = useEntity();

  if (sortedTierData?.length === 1 /* No tiers, show old way */) {
    return (
      <FnrTypography
        data-cy="TierPriceViewer_NoTiers"
        color="textLight"
      >{`$${formatCurrencyWithCommas(Number(minCost) / 100)}${
        suggestedDonation ? " suggested donation" : ""
      } per ${entity}`}</FnrTypography>
    );
  }

  return (
    <FnrAccordion
      label={
        <FnrTypography color="textLight">
          {`${
            suggestedDonation ? "Suggested donations start at" : "Starts at"
          } $${formatCurrencyWithCommas(Number(minCost) / 100)}`}
        </FnrTypography>
      }
      defaultClosed
      dark
      alignContentToLabel
      data-cy="TierPriceViewer_AccordionPanel"
      // noActiveMargin
      marginCorrection
    >
      <Grid data-cy="TierPriceViewer_Grid">
        {sortedTierData?.map(({ amount, numberOfSlots, id }, idx) => (
          <Tier
            key={id}
            amount={amount}
            numberOfSlots={numberOfSlots}
            numberOfSlotsBelow={sortedTierData?.[idx - 1]?.numberOfSlots}
          />
        ))}
      </Grid>
    </FnrAccordion>
  );
};

const TierPriceViewer = ({ tierData, suggestedDonation, entity = "slot" }) => (
  <TierPricingProvider
    tierData={tierData}
    showCard={false}
    suggestedDonation={suggestedDonation}
    entity={entity}
  >
    <TierPriceViewerContents />
  </TierPricingProvider>
);

TierPriceViewer.propTypes = {
  tierData: arrayOf(
    shape({
      numberOfSlots: oneOfType([number, string]),
      amount: oneOfType([number, string]),
      id: oneOfType([number, string]),
      ID: oneOfType([number, string]),
    })
  ),
  suggestedDonation: bool,
  entity: string,
};

export default TierPriceViewer;
